import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { formatTableText } from '@/lib/tableUtils';

const RakeMovementReportTable = ({ reports = [] }) => {
  const formatVal = (val) => {
    if (val === null || val === undefined || val === 'N/A' || val === 'None' || val === 'null' || val === '') {
      return '--';
    }
    const formatted = formatTableText(val);
    return formatted === 'N/A' ? '--' : formatted;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return '--';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '--';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '--';
    }
  };

  const getVarianceBadge = (varianceType, qty) => {
    if (qty === null || qty === undefined || qty === 'N/A' || qty === '') return '--';
    const num = parseFloat(qty);
    if (isNaN(num)) return '--';
    if (varianceType === 'EXCESS') {
      return (
        <Badge className="shadow-none focus:ring-0 focus:ring-offset-0 bg-amber-100 hover:bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 font-semibold border-amber-200 inline-block text-center min-w-[80px]">
          +{num.toFixed(2)}
        </Badge>
      );
    }
    if (varianceType === 'SHORTAGE') {
      return (
        <Badge className="shadow-none focus:ring-0 focus:ring-offset-0 bg-red-100 hover:bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 font-semibold border-red-200 inline-block text-center min-w-[80px]">
          {num.toFixed(2)}
        </Badge>
      );
    }
    if (varianceType === 'BALANCED') {
      return (
        <Badge className="shadow-none focus:ring-0 focus:ring-offset-0 bg-emerald-100 hover:bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 font-semibold border-emerald-200 inline-block text-center min-w-[80px]">
          0.00
        </Badge>
      );
    }
    return '--';
  };

  const hasValue = (key) => {
    return reports.some((report) => {
      const val = report[key];
      return val !== null && val !== undefined && val !== 'N/A' && val !== 'None' && val !== 'null' && val !== '';
    });
  };

  const hasVarianceValue = () => {
    return reports.some((report) => {
      const val = report.variance_type;
      return val !== null && val !== undefined && val !== 'N/A' && val !== 'None' && val !== 'null' && val !== '';
    });
  };

  if (!reports || reports.length === 0) {
    return null;
  }

  return (
    <div className="rounded-md border overflow-hidden w-full">
      <div className="max-h-[calc(100vh-250px)] overflow-auto w-full">
        <Table>
          <TableHeader className="sticky top-0 z-10">
            <TableRow>
              <TableHead className="h-10 px-4 py-1 min-w-[150px]">Trip Number</TableHead>
              {hasValue('port_name') && (
                <TableHead className="h-10 px-4 py-1 min-w-[120px]">Port</TableHead>
              )}
              <TableHead className="h-10 px-4 py-1 min-w-[110px]">Date</TableHead>
              {hasValue('destination_state') && (
                <TableHead className="h-10 px-4 py-1 min-w-[130px]">State</TableHead>
              )}
              {hasValue('destination_district') && (
                <TableHead className="h-10 px-4 py-1 min-w-[130px]">District</TableHead>
              )}
              {hasValue('rake_point_name') && (
                <TableHead className="h-10 px-4 py-1 min-w-[130px]">Rake Point</TableHead>
              )}
              <TableHead className="h-10 px-4 py-1 min-w-[120px]">Product</TableHead>
              <TableHead className="h-10 px-4 py-1 min-w-[100px]">Baseline Qty</TableHead>
              {hasValue('receipt_qty') && (
                <TableHead className="h-10 px-4 py-1 min-w-[100px]">Receipt</TableHead>
              )}
              {hasValue('missing_wagons') && (
                <TableHead className="h-10 px-4 py-1 min-w-[100px]">Missing</TableHead>
              )}
              {hasVarianceValue() && (
                <TableHead className="h-10 px-4 py-1 min-w-[100px]">Variance</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((report, index) => (
              <TableRow key={report.rake_movement_id ? `${report.rake_movement_id}-${index}` : index} className="hover:bg-transparent border-border">
                <TableCell className="h-10 px-4 py-1 font-medium">{formatVal(report.trip_number)}</TableCell>
                {hasValue('port_name') && (
                  <TableCell className="h-10 px-4 py-1">{formatVal(report.port_name)}</TableCell>
                )}
                <TableCell className="h-10 px-4 py-1">{formatDate(report.date)}</TableCell>
                {hasValue('destination_state') && (
                  <TableCell className="h-10 px-4 py-1">{formatVal(report.destination_state)}</TableCell>
                )}
                {hasValue('destination_district') && (
                  <TableCell className="h-10 px-4 py-1">{formatVal(report.destination_district)}</TableCell>
                )}
                {hasValue('rake_point_name') && (
                  <TableCell className="h-10 px-4 py-1">{formatVal(report.rake_point_name)}</TableCell>
                )}
                <TableCell className="h-10 px-4 py-1">{formatVal(report.product_type_name)}</TableCell>
                <TableCell className="h-10 px-4 py-1">{formatVal(report.baseline_qty_mt)}</TableCell>
                {hasValue('receipt_qty') && (
                  <TableCell className="h-10 px-4 py-1">{formatVal(report.receipt_qty)}</TableCell>
                )}
                {hasValue('missing_wagons') && (
                  <TableCell className="h-10 px-4 py-1">{formatVal(report.missing_wagons)}</TableCell>
                )}
                {hasVarianceValue() && (
                  <TableCell className="h-10 px-4 py-1">
                    {getVarianceBadge(report.variance_type, report.variance_qty_mt)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default RakeMovementReportTable;
